/**
 * Habit — отдельная сущность, НЕ связанная с задачами/идеями.
 * Frequency хранится структурой (объектом); на сервер/payload может уезжать JSON.
 */

export type HabitFrequency =
  | { type: 'interval'; interval: number }
  | { type: 'weekdays'; weekdays: number[] }
  | { type: 'per_week'; target: number };

export interface HabitEntity {
  id: string;
  user_id: string;
  name: string;
  description: string;
  color: string;
  frequency: HabitFrequency;
  /** Календарная дата YYYY-MM-DD. */
  start_date: string;
  /** null — активна; строка ISO — на паузе. */
  paused_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  server_revision: number | null;
  dirty: boolean;
}

export interface HabitCompletionEntity {
  id: string;
  habit_id: string;
  /** Календарная дата YYYY-MM-DD. */
  completion_date: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  server_revision: number | null;
  dirty: boolean;
}

/** Составной id completion: habit_id + дата (как task completion). */
export function habitCompletionId(habitId: string, completionDate: string): string {
  return `${habitId}:${completionDate}`;
}

export function isHabitPaused(habit: HabitEntity): boolean {
  return habit.paused_at !== null;
}

function asInt(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  const truncated = Math.trunc(value);
  if (truncated < min || truncated > max) {
    return fallback;
  }
  return truncated;
}

function asWeekdays(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const cleaned = [
    ...new Set(
      value.filter(
        (item): item is number => typeof item === 'number' && Number.isInteger(item) && item >= 0 && item <= 6,
      ),
    ),
  ].sort((a, b) => a - b);
  return cleaned;
}

/** Валидация частоты из payload/Dexie. Битые значения → безопасный дефолт. */
export function asHabitFrequency(value: unknown): HabitFrequency {
  if (typeof value === 'string') {
    try {
      return asHabitFrequency(JSON.parse(value) as unknown);
    } catch {
      return { type: 'interval', interval: 1 };
    }
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return { type: 'interval', interval: 1 };
  }
  const obj = value as Record<string, unknown>;
  const type = obj['type'];
  if (type === 'interval') {
    return { type: 'interval', interval: asInt(obj['interval'], 1, 1, 365) };
  }
  if (type === 'weekdays') {
    const weekdays = asWeekdays(obj['weekdays']);
    return { type: 'weekdays', weekdays: weekdays.length > 0 ? weekdays : [0] };
  }
  if (type === 'per_week') {
    return { type: 'per_week', target: asInt(obj['target'], 3, 1, 7) };
  }
  return { type: 'interval', interval: 1 };
}

export function serializeHabitFrequency(frequency: HabitFrequency): string {
  return JSON.stringify(frequency);
}
